<script lang="ts">
  /**
   * Freja eID-koppling för admin-konto (S-04-37). Renderas i Settings →
   * Säkerhet, under TOTP-kortet.
   *
   * Flöde:
   *  1. Status hämtas vid mount — bunden/obunden/avstängd (503 = Freja
   *     ej aktiverat i miljön) eller fel (nätverk/session).
   *  2. Koppla: TOTP-kod (step-up) → backend startar Freja-transaktion →
   *     QR renderas klient-side → poll var 3:e sekund tills bunden.
   *  3. Koppla bort: bekräftelsesteg → TOTP-kod → unbind.
   *
   * Hard-rules: inga tokens/secrets klient-side; alla mutationer bär
   * CSRF-header via frejaBindClient; användartexter på svenska.
   *
   * Tillgänglighet: en persistent live-region (utanför alla {#if})
   * annonserar state-byten. En region som rivs och återskapas med sitt
   * innehåll läses opålitligt av skärmläsare.
   */
  import QRCode from 'qrcode';
  import { frejaBindInitiate, frejaBindPoll, frejaStatus, frejaUnbind } from './frejaBindClient';

  type Mode = 'loading' | 'disabled' | 'error' | 'unbound' | 'bound' | 'binding' | 'confirmUnbind';

  let mode = $state<Mode>('loading');
  let mfaCode = $state('');
  let errorMessage: string | null = $state(null);
  let qrUrl = $state('');
  let deepLink = $state('');
  let delivered = $state(false);
  let isWorking = $state(false);
  let generation = 0;

  const statusText = $derived(
    mode === 'bound'
      ? 'Freja eID är nu kopplat till ditt konto.'
      : mode === 'binding'
        ? qrUrl
          ? delivered
            ? 'Öppnad i mobilen — godkänn i Freja-appen.'
            : 'Väntar på att du skannar QR-koden.'
          : 'Startar koppling…'
        : '',
  );

  $effect(() => {
    void refreshStatus();
  });

  async function refreshStatus(): Promise<void> {
    const result = await frejaStatus();
    if (result.kind === 'disabled') {
      mode = 'disabled';
    } else if (result.kind === 'ok') {
      mode = result.bound ? 'bound' : 'unbound';
    } else {
      // Nätverks-/sessionsfel är INTE samma sak som avstängd Freja —
      // eget läge med retry, annars ser admin två motstridiga budskap.
      mode = 'error';
      errorMessage = result.message;
    }
  }

  async function retryStatus(): Promise<void> {
    errorMessage = null;
    mode = 'loading';
    await refreshStatus();
  }

  function validCode(): string | null {
    const code = mfaCode.trim();
    return /^\d{6}$/.test(code) ? code : null;
  }

  async function startBind(): Promise<void> {
    if (isWorking) return;
    errorMessage = null;
    const code = validCode();
    if (!code) {
      errorMessage = 'Koden måste vara sex siffror.';
      return;
    }
    isWorking = true;
    const gen = ++generation;
    try {
      const init = await frejaBindInitiate(code);
      mfaCode = '';
      if (gen !== generation) return;
      if (init.kind === 'failure') {
        errorMessage = init.message;
        return;
      }
      try {
        qrUrl = await QRCode.toDataURL(init.qrData, {
          errorCorrectionLevel: 'M',
          margin: 2,
          width: 480,
          color: { dark: '#0a0a0a', light: '#f2ece0' },
        });
        deepLink = init.qrData;
      } catch {
        errorMessage = 'Kunde inte skapa QR-kod. Försök igen.';
        return;
      }
      mode = 'binding';
      delivered = false;

      const startedAt = Date.now();
      while (gen === generation && mode === 'binding' && Date.now() - startedAt < 150_000) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        if (gen !== generation || mode !== 'binding') return;
        const result = await frejaBindPoll(init.authRef);
        if (gen !== generation || mode !== 'binding') return;
        if (result.kind === 'bound') {
          qrUrl = '';
          deepLink = '';
          mode = 'bound';
          return;
        }
        if (result.kind === 'failure') {
          errorMessage = result.message;
          qrUrl = '';
          deepLink = '';
          mode = 'unbound';
          return;
        }
        delivered = result.status === 'DELIVERED_TO_MOBILE';
      }
      if (gen === generation && mode === 'binding') {
        errorMessage = 'QR-koden har gått ut. Börja om för att få en ny.';
        qrUrl = '';
        deepLink = '';
        mode = 'unbound';
      }
    } finally {
      isWorking = false;
    }
  }

  function cancelBind(): void {
    generation += 1;
    qrUrl = '';
    deepLink = '';
    mode = 'unbound';
    errorMessage = null;
  }

  async function submitUnbind(): Promise<void> {
    if (isWorking) return;
    errorMessage = null;
    const code = validCode();
    if (!code) {
      errorMessage = 'Koden måste vara sex siffror.';
      return;
    }
    isWorking = true;
    try {
      const result = await frejaUnbind(code);
      mfaCode = '';
      if (result.kind === 'ok') {
        mode = 'unbound';
      } else {
        errorMessage = result.message;
        mode = 'confirmUnbind';
      }
    } finally {
      isWorking = false;
    }
  }
</script>

<div class="settings-card">
  <h3>Freja eID</h3>

  <!-- Persistent live-region: ligger utanför alla {#if} så skärmläsare
       får en stabil region att annonsera ur. -->
  <p class="sr-status" role="status" aria-live="polite">{statusText}</p>

  {#if errorMessage}
    <div class="alert error" role="alert">{errorMessage}</div>
  {/if}

  {#if mode === 'loading'}
    <p>Hämtar status…</p>
  {:else if mode === 'disabled'}
    <p>Freja eID är inte aktiverat för den här installationen.</p>
  {:else if mode === 'error'}
    <p>Kunde inte hämta Freja-status.</p>
    <button type="button" class="btn secondary" onclick={retryStatus}> Försök igen </button>
  {:else if mode === 'bound'}
    <p>
      <span class="ok-mark" aria-hidden="true">✓</span> Freja eID är kopplat till ditt konto. Du kan
      logga in i Lyktan genom att skanna QR-koden på inloggningssidan med Freja-appen.
    </p>
    <button type="button" class="btn danger" onclick={() => (mode = 'confirmUnbind')}>
      Koppla bort Freja eID
    </button>
  {:else if mode === 'confirmUnbind'}
    <p>
      Kopplingen tas bort och du loggar in med lösenord och MFA-kod igen. Du kan koppla Freja på
      nytt när du vill.
    </p>
    <form
      onsubmit={(event) => {
        event.preventDefault();
        void submitUnbind();
      }}
      novalidate
    >
      <label>
        <span>MFA-kod</span>
        <input
          type="text"
          inputmode="numeric"
          maxlength="6"
          autocomplete="one-time-code"
          bind:value={mfaCode}
          disabled={isWorking}
        />
      </label>
      <div class="actions">
        <button
          type="button"
          class="btn secondary"
          onclick={() => {
            mode = 'bound';
            mfaCode = '';
            errorMessage = null;
          }}
        >
          Avbryt
        </button>
        <button type="submit" class="btn danger" disabled={isWorking || mfaCode.length !== 6}>
          {isWorking ? 'Kopplar bort…' : 'Koppla bort'}
        </button>
      </div>
    </form>
  {:else if mode === 'binding'}
    {#if qrUrl}
      <div class="qr-frame">
        <img src={qrUrl} alt="QR-kod för att koppla Freja eID" />
      </div>
      <p class="hint">
        Skanna med Freja eID-appen och godkänn. Koden gäller i cirka två minuter.
      </p>
      <a class="devicelink" href={deepLink}>Öppna Freja eID på den här enheten</a>
    {/if}
    <button type="button" class="btn secondary" onclick={cancelBind}> Avbryt </button>
  {:else}
    <p>
      Koppla ditt Freja eID för att kunna logga in i Lyktan med QR-skanning i stället för lösenord
      och MFA-kod. Kopplingen kräver din MFA-kod.
    </p>
    <form
      onsubmit={(event) => {
        event.preventDefault();
        void startBind();
      }}
      novalidate
    >
      <label>
        <span>MFA-kod</span>
        <input
          type="text"
          inputmode="numeric"
          maxlength="6"
          autocomplete="one-time-code"
          bind:value={mfaCode}
          disabled={isWorking}
        />
      </label>
      <button type="submit" class="btn primary" disabled={isWorking || mfaCode.length !== 6}>
        {isWorking ? 'Startar…' : 'Koppla Freja eID'}
      </button>
    </form>
  {/if}
</div>

<style>
  /* Matchar .settings-card i SettingsView så kortet inte avviker från
     sitt syskonkort (Tvåfaktor) i samma sektion. */
  .settings-card {
    background: var(--bg-elev);
    border: 1px solid var(--rule);
    border-radius: 6px;
    padding: 1.5rem;
    color: var(--text);
    display: grid;
    gap: 1rem;
    justify-items: start;
  }
  h3 {
    margin: 0;
    font-size: 1.05rem;
  }
  p {
    margin: 0;
    color: var(--text-dim);
    line-height: 1.55;
  }
  .ok-mark {
    color: #2e8c57;
    font-weight: 700;
  }
  .alert.error {
    padding: 0.6rem 0.9rem;
    border-radius: 4px;
    font-size: 0.92rem;
    color: var(--danger);
    background: var(--danger-soft);
    border: 1px solid var(--danger);
  }
  form {
    display: grid;
    gap: 0.75rem;
    justify-items: start;
  }
  label {
    display: grid;
    gap: 0.35rem;
    font-size: 0.8rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--text-dim);
    font-weight: 600;
  }
  input {
    background: var(--bg);
    color: var(--text);
    border: 1px solid var(--rule);
    border-radius: 4px;
    padding: 0.5rem 0.7rem;
    font: inherit;
    width: 9ch;
    text-transform: none;
    letter-spacing: 0.2em;
  }
  input:focus {
    outline: 2px solid var(--accent);
    outline-offset: 0;
    border-color: var(--accent);
  }
  .actions {
    display: flex;
    gap: 0.75rem;
  }
  .btn {
    appearance: none;
    border: 1px solid transparent;
    border-radius: 4px;
    padding: 0.55rem 1rem;
    font: inherit;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
  }
  .btn.primary {
    background: var(--accent);
    color: var(--bg);
    border-color: var(--accent);
  }
  .btn.secondary {
    background: transparent;
    color: var(--accent);
    border-color: var(--accent);
  }
  /* Samma danger-behandling som UsersView använder för borttag. */
  .btn.danger {
    background: transparent;
    color: var(--danger);
    border-color: var(--danger);
  }
  .btn.danger:hover:not(:disabled),
  .btn.danger:focus-visible:not(:disabled) {
    background: var(--danger-soft);
  }
  .btn:focus-visible {
    outline: 2px solid var(--glow);
    outline-offset: 2px;
  }
  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .qr-frame {
    background: var(--qr-paper);
    padding: 0.75rem;
    border-radius: 4px;
    line-height: 0;
  }
  .qr-frame img {
    width: 200px;
    height: 200px;
    image-rendering: pixelated;
  }
  .hint {
    font-size: 0.9rem;
  }
  .devicelink {
    color: var(--accent);
    font-size: 0.9rem;
  }
  .devicelink:focus-visible {
    outline: 2px solid var(--glow);
    outline-offset: 2px;
  }
</style>
